'use client';

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ControllerRenderProps } from 'react-hook-form';
import type { LoginInput } from '../types/login-schema';

interface PasswordFieldProps {
  field: ControllerRenderProps<LoginInput, 'password'>;
  label?: string;
  disabled?: boolean;
  error?: string;
}

export function PasswordField({
  field,
  label = 'Password',
  disabled,
  error,
}: PasswordFieldProps) {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-foreground">{label}</label>
      <div className="relative">
        <input
          {...field}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          disabled={disabled}
          className={cn(
            'h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm text-foreground transition-colors placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:border-ring',
            error && 'border-destructive focus:ring-destructive',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          placeholder="••••••••"
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          tabIndex={-1}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground focus:outline-none"
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            <EyeOff className="h-4 w-4" />
          ) : (
            <Eye className="h-4 w-4" />
          )}
        </button>
      </div>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
